import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { Loader2, TrendingUp, LineChart, Search, Megaphone, Wand2 } from "lucide-react";

/**
 * Phase 4 Intelligence panels — one component, mode switches the endpoint.
 * mode: "trend" | "market" | "seo" | "marketing"
 */
export function IntelligenceAnalystPanel({ mode }) {
  const { lang } = useLang();
  const [input, setInput] = useState("");
  const [objective, setObjective] = useState("conversion");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const meta = {
    trend:     { Icon: TrendingUp, title: t(lang, "p4_trend_title"),     ph: t(lang, "p4_trend_ph"),     endpoint: "/phase4/trend/analyze",     body: (v) => ({ seed: v }) },
    market:    { Icon: LineChart,  title: t(lang, "p4_market_title"),    ph: t(lang, "p4_market_ph"),    endpoint: "/phase4/market/analyze",    body: (v) => ({ product_or_niche: v }) },
    seo:       { Icon: Search,     title: t(lang, "p4_seo_title"),       ph: t(lang, "p4_seo_ph"),       endpoint: "/phase4/seo/analyze",       body: (v) => ({ handle: v }) },
    marketing: { Icon: Megaphone,  title: t(lang, "p4_mkt_title"),       ph: t(lang, "p4_mkt_ph"),       endpoint: "/phase4/marketing/analyze", body: (v) => ({ product: v, objective }) },
  }[mode];

  const { Icon } = meta;

  const loadHistory = async () => {
    const { data } = await api.get(`/phase4/reports?kind=${mode}&limit=20`);
    setHistory(data.reports || []);
  };

  useEffect(() => { loadHistory(); }, [mode]);

  const run = async () => {
    if (!input.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(meta.endpoint, meta.body(input.trim()));
      setResult(data.result);
      await loadHistory();
      toast.success(t(lang, "p4_done"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hq-card overflow-hidden" data-testid={`p4-${mode}-panel`}>
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Icon size={14} className="text-[color:var(--hq-cyan)]" />
          <div className="font-display text-lg">{meta.title}</div>
          <span className="ml-auto font-mono text-[10px] uppercase text-white/40">GPT-5.2</span>
        </div>

        <div className="flex gap-2 flex-wrap">
          <input
            data-testid={`p4-${mode}-input`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={meta.ph}
            className="hq-input flex-1 min-w-[280px]"
            onKeyDown={(e) => { if (e.key === "Enter") run(); }}
          />
          {mode === "marketing" && (
            <select
              data-testid="p4-mkt-objective"
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              className="hq-input"
            >
              <option value="awareness">{t(lang, "p4_obj_awareness")}</option>
              <option value="conversion">{t(lang, "p4_obj_conversion")}</option>
              <option value="retention">{t(lang, "p4_obj_retention")}</option>
            </select>
          )}
          <button
            data-testid={`p4-${mode}-run-btn`}
            onClick={run}
            disabled={busy || !input.trim()}
            className="hq-btn hq-btn-primary disabled:opacity-50"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            {busy ? t(lang, "p4_running") : t(lang, "p4_run")}
          </button>
        </div>
      </div>

      {result && (
        <div className="p-5 border-b border-white/10 space-y-3" data-testid={`p4-${mode}-result`}>
          {mode === "trend" && <TrendResult r={result} lang={lang} />}
          {mode === "market" && <MarketResult r={result} lang={lang} />}
          {mode === "seo" && <SEOResult r={result} lang={lang} />}
          {mode === "marketing" && <MarketingResult r={result} lang={lang} />}
        </div>
      )}

      {history.length > 0 && (
        <div className="p-5 space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
            {t(lang, "p4_history")}
          </div>
          {history.slice(0, 8).map((h) => (
            <button
              key={`${h.kind}-${h.report_key}-${h.generated_at}`}
              onClick={() => setResult(h.result)}
              className="w-full text-left hq-nav-item"
              data-testid={`p4-${mode}-history-${h.report_key}`}
            >
              <span className="flex-1 truncate text-xs">{h.report_key}</span>
              <span className="font-mono text-[10px] text-white/40">
                {new Date(h.generated_at).toLocaleDateString()}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-white/5 text-xs">
      <span className="text-white/50 font-mono uppercase text-[10px]">{k}</span>
      <span className="text-white text-right">{v}</span>
    </div>
  );
}

function TrendResult({ r, lang }) {
  // Map trend name/keywords to a curated Unsplash photo so each trend block
  // gets a distinct visual identity — no LLM image gen cost.
  const photoFor = (trendName = "") => {
    const s = trendName.toLowerCase();
    const map = [
      ["home", "photo-1616486338812-3dadae4b4ace"],
      ["decor", "photo-1616486338812-3dadae4b4ace"],
      ["candle", "photo-1602523498340-434e5b39c74d"],
      ["jewel", "photo-1617038220319-276d3cfab638"],
      ["earring", "photo-1617038220319-276d3cfab638"],
      ["necklace", "photo-1611591437281-460bfbe1220a"],
      ["ring", "photo-1603561591411-07134e71a2a9"],
      ["bracelet", "photo-1611085583191-a3b181a88401"],
      ["crystal", "photo-1615486363873-f61bbec7d09b"],
      ["stone", "photo-1615486363873-f61bbec7d09b"],
      ["pet", "photo-1583337130417-3346a1be7dee"],
      ["cat", "photo-1592194996308-7b43878e84a6"],
      ["dog", "photo-1583337130417-3346a1be7dee"],
      ["gift", "photo-1549465220-1a8b9238cd48"],
      ["cozy", "photo-1519710164239-da123dc03ef4"],
      ["hygge", "photo-1519710164239-da123dc03ef4"],
      ["desk", "photo-1611532736597-de2d4265fba3"],
      ["kitchen", "photo-1556909114-f6e7ad7d3136"],
      ["bath", "photo-1600566752355-35792bedcfea"],
      ["yoga", "photo-1544367567-0f2fcb009e0b"],
      ["wellness", "photo-1544367567-0f2fcb009e0b"],
      ["book", "photo-1544716278-ca5e3f4abd8c"],
      ["floral", "photo-1519378058457-4c29a0a2efac"],
      ["flower", "photo-1519378058457-4c29a0a2efac"],
      ["minimal", "photo-1524758631624-e2822e304c36"],
      ["boho", "photo-1524758631624-e2822e304c36"],
      ["japandi", "photo-1524758631624-e2822e304c36"],
    ];
    for (const [kw, id] of map) if (s.includes(kw)) return `https://images.unsplash.com/${id}?auto=format&fit=crop&w=520&q=70`;
    // Deterministic fallback so each trend still gets a stable image.
    const fallbacks = [
      "photo-1519710164239-da123dc03ef4",  // cozy home
      "photo-1524758631624-e2822e304c36",  // minimal
      "photo-1616486338812-3dadae4b4ace",  // decor
      "photo-1549465220-1a8b9238cd48",     // gift
    ];
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return `https://images.unsplash.com/${fallbacks[h % fallbacks.length]}?auto=format&fit=crop&w=520&q=70`;
  };

  const trends = r.trends || [];
  return (
    <>
      {r.summary && (
        <div className="border-l-2 border-[color:var(--hq-cyan)] pl-3 py-1 mb-2">
          <p className="text-sm text-white/85 leading-snug">{r.summary}</p>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {trends.map((tr, i) => {
          const momentum = Math.round((tr.momentum || 0) * 100);
          const confidence = Math.round((tr.confidence || 0) * 100);
          const momentumTone = momentum >= 70 ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
            : momentum >= 40 ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
            : "bg-white/10 text-white/60 border-white/20";
          return (
            <div key={i}
                 data-testid={`p4-trend-card-${i}`}
                 className="group border border-white/10 rounded-lg overflow-hidden bg-[#0a0a0a] hover:border-[color:var(--hq-cyan)]/50 transition-colors duration-300">
              {/* Hero image */}
              <div className="aspect-[4/3] relative overflow-hidden bg-black">
                <img src={photoFor(tr.name || "")}
                     alt={tr.name || "trend"}
                     loading="lazy"
                     className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className={`absolute top-2 right-2 font-mono text-[9px] uppercase tracking-wider border px-2 py-0.5 rounded-full ${momentumTone}`}>
                  {momentum}% momentum
                </div>
                <div className="absolute bottom-2 left-3 right-3">
                  <div className="font-display text-sm text-white leading-tight drop-shadow-lg">
                    {tr.name}
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-3 space-y-2">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase text-white/50">
                  <span>confiança</span>
                  <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-[color:var(--hq-cyan)]" style={{width: `${confidence}%`}} />
                  </div>
                  <span className="text-[color:var(--hq-cyan)]">{confidence}%</span>
                </div>
                {tr.opportunity_notes && (
                  <p className="text-xs text-white/75 leading-snug line-clamp-3">
                    {tr.opportunity_notes}
                  </p>
                )}
                {tr.risk_notes && (
                  <p className="text-[11px] text-amber-300/70 leading-snug line-clamp-2">
                    ⚠ {tr.risk_notes}
                  </p>
                )}
                {(tr.example_products || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1 border-t border-white/5">
                    {(tr.example_products || []).slice(0, 4).map((p, j) => (
                      <span key={j}
                            className="font-mono text-[9px] px-2 py-0.5 rounded-full border border-white/15 text-white/70 bg-white/[0.03]">
                        {p}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {r.recommended_action && (
        <div className="mt-3 rounded-lg border border-[color:var(--hq-cyan)]/40 bg-[color:var(--hq-cyan)]/[0.05] p-3 text-xs text-[color:var(--hq-cyan)] font-mono flex items-start gap-2">
          <span className="text-[color:var(--hq-cyan)]">→</span>
          <span className="flex-1 text-white/85 normal-case tracking-normal">{r.recommended_action}</span>
        </div>
      )}
    </>
  );
}

function MarketResult({ r, lang }) {
  const chf = (n) => new Intl.NumberFormat("de-CH").format(n || 0);
  return (
    <>
      <div className="grid gap-4 md:grid-cols-3">
        <div className="border border-white/10 p-3">
          <div className="text-[10px] font-mono uppercase text-white/40 mb-1">TAM CH</div>
          <div className="text-xl font-mono">CHF {chf(r.tam_ch_chf_year)}</div>
        </div>
        <div className="border border-white/10 p-3">
          <div className="text-[10px] font-mono uppercase text-white/40 mb-1">SAM CH</div>
          <div className="text-xl font-mono">CHF {chf(r.sam_ch_chf_year)}</div>
        </div>
        <div className="border border-white/10 p-3">
          <div className="text-[10px] font-mono uppercase text-white/40 mb-1">SAM EU</div>
          <div className="text-xl font-mono">CHF {chf(r.sam_eu_chf_year)}</div>
        </div>
      </div>
      <KV k="Density" v={r.competitor_density} />
      <div className="text-xs text-white/50 font-mono uppercase mt-2">Top competitors</div>
      <div className="flex flex-wrap gap-1">
        {(r.top_competitors || []).map((c, i) => (
          <span key={i} className="font-mono text-[10px] px-2 py-0.5 border border-white/15">{c}</span>
        ))}
      </div>
      <div className="text-xs text-white/50 font-mono uppercase mt-2">Gaps</div>
      <ul className="text-xs text-white/80 list-disc ml-5">
        {(r.positioning_gaps || []).map((g, i) => <li key={i}>{g}</li>)}
      </ul>
      <div className="text-xs text-[color:var(--hq-warning)]">{r.risk_summary}</div>
    </>
  );
}

function SEOResult({ r, lang }) {
  const primary = (r.primary_keyword && (r.primary_keyword[lang] || r.primary_keyword.en)) || "";
  return (
    <>
      <div className="border border-[color:var(--hq-cyan)]/40 p-3">
        <div className="text-[10px] font-mono uppercase text-white/40">Primary keyword ({lang})</div>
        <div className="text-lg font-display">{primary}</div>
      </div>
      <KV k={`Improved SEO title (${lang})`} v={(r.improved_seo_title || {})[lang] || (r.improved_seo_title || {}).en} />
      <div className="text-xs text-white/70">
        <span className="font-mono uppercase text-white/40">Meta ({lang}): </span>
        {(r.improved_meta_description || {})[lang] || (r.improved_meta_description || {}).en}
      </div>
      <div className="text-xs text-white/50 font-mono uppercase mt-2">Keyword clusters</div>
      <div className="grid gap-2 md:grid-cols-2">
        {(r.keyword_clusters || []).map((c, i) => (
          <div key={i} className="border border-white/10 p-2">
            <div className="font-mono text-[10px] uppercase text-[color:var(--hq-cyan)] mb-1">{c.language}</div>
            <div className="flex flex-wrap gap-1">
              {(c.keywords || []).map((k, j) => (
                <span key={j} className="text-[11px] px-2 py-0.5 border border-white/15">{k}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="text-xs text-[color:var(--hq-cyan)]">Angle: {r.content_angle}</div>
    </>
  );
}

function MarketingResult({ r, lang }) {
  const rangeCHF = (arr) => Array.isArray(arr) && arr.length === 2 ? `CHF ${arr[0]}–${arr[1]}` : "—";
  return (
    <>
      <KV k="Objective" v={r.objective} />
      <div className="text-xs text-white/50 font-mono uppercase mt-2">Channels</div>
      {(r.channels || []).map((c, i) => (
        <div key={i} className="flex justify-between text-xs py-1 border-b border-white/5">
          <span>{c.name}</span>
          <span className="font-mono text-[color:var(--hq-cyan)]">{c.budget_share_pct}%</span>
        </div>
      ))}
      <div className="text-xs text-white/50 font-mono uppercase mt-2">Audiences</div>
      {(r.audiences || []).map((a, i) => (
        <div key={i} className="border border-white/10 p-2 mb-1">
          <div className="text-sm">{a.segment}</div>
          <div className="text-white/60 text-xs">{a.angle}</div>
          <div className="font-mono text-[10px] text-[color:var(--hq-cyan)]">
            Expected CTR {Math.round((a.expected_ctr || 0) * 100)}%
          </div>
        </div>
      ))}
      <div className="text-xs text-white/50 font-mono uppercase mt-2">Hook ({lang})</div>
      <div className="border border-white/10 p-2 text-sm italic">
        {(r.hooks || {})[lang] || (r.hooks || {}).en}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2">
        <KV k="CAC" v={rangeCHF(r.expected_cac_chf)} />
        <KV k="LTV" v={rangeCHF(r.expected_ltv_chf)} />
        <KV k="Target ROAS" v={r.target_roas ? `${r.target_roas}×` : "—"} />
      </div>
      <div className="text-xs text-[color:var(--hq-cyan)]">→ {r.next_step}</div>
    </>
  );
}
